import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchCarserviceCities } from "../../services/publicCrmApi";

/**
 * Serviceable-cities context for the /carservice landing page.
 *
 * Cities are fetched once from the CRM public endpoint
 * `GET /api/public/carservice/cities` — never hardcoded. New cities the ops
 * team turns on appear in the dropdown automatically without any code
 * change.
 *
 * Selection priority (per product spec):
 *   1. URL param `?city=<slug>` — case-insensitive, matched against the
 *      lowercase city name. This is how ad campaigns carry their city
 *      (e.g. Google Ads Hyderabad → /carservice?city=hyderabad).
 *   2. User's dropdown selection (persisted to sessionStorage for the
 *      duration of the tab so refreshes don't lose state).
 *   3. Neither → `selectedCity` stays `null`; the booking flow must prompt
 *      "Select your city" before submission. NEVER default to Bengaluru.
 *
 * Consumers: <CarServiceHeader> (dropdown UI), <CarServiceBookingModal>
 * (submits city in lead body), <CarServiceStatsStrip> (city-scoped stats).
 *
 * If the /cities endpoint fails, `cities` stays [] and consumers must
 * degrade gracefully (show "Select your city" prompt, no dropdown).
 */

const STORAGE_KEY = "wd_cs_selected_city";

const CarServiceCityContext = createContext({
  cities: [],
  loading: true,
  error: null,
  selectedCity: null,   // { id, name } | null
  setSelectedCity: () => {},
  citySource: null,     // "url" | "user" | "session" | null
});

function slugify(name) {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");
}

function readUrlCityParam() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("city");
  return raw ? slugify(raw) : null;
}

function readSessionCity() {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSessionCity(city) {
  if (typeof sessionStorage === "undefined") return;
  try {
    if (city) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(city));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop — private mode / quota */
  }
}

export const CarServiceCityProvider = ({ children }) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCity, setSelectedCityState] = useState(null);
  const [citySource, setCitySource] = useState(null);

  // 1. Fetch cities from CRM (no auth) on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCarserviceCities();
        const list = Array.isArray(data?.cities) ? data.cities : [];
        if (cancelled) return;
        setCities(list);

        // 2. Resolve initial selection: URL > session > null.
        const urlSlug = readUrlCityParam();
        if (urlSlug) {
          // Match on the API `slug` field first (case-insensitive), then
          // fall back to a slug derived from the display name. This is how
          // Google Ads landing URLs (?city=mumbai / ?city=delhi) pre-select
          // the dropdown — robust even if a city's slug differs from its name.
          const match = list.find(
            (c) =>
              String(c.slug || "").toLowerCase() === urlSlug ||
              slugify(c.name) === urlSlug,
          );
          if (match) {
            setSelectedCityState(match);
            setCitySource("url");
            writeSessionCity(match);
            return;
          }
        }
        const cached = readSessionCity();
        if (cached?.id) {
          // Re-hydrate from the live list so cached stale names/metadata
          // don't leak through if the CRM updated a city record between
          // sessions. If the cached id is no longer serviceable, drop it.
          const liveMatch = list.find((c) => c.id === cached.id);
          if (liveMatch) {
            setSelectedCityState(liveMatch);
            setCitySource("session");
            writeSessionCity(liveMatch);
          } else {
            writeSessionCity(null);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setSelectedCity = useCallback((cityOrId) => {
    // Accept either a full city object or an id string, for flexibility.
    let next = null;
    if (cityOrId && typeof cityOrId === "object") {
      next = cityOrId;
    } else if (cityOrId) {
      next = cities.find((c) => c.id === cityOrId) || null;
    }
    setSelectedCityState(next);
    setCitySource(next ? "user" : null);
    writeSessionCity(next);
  }, [cities]);

  const value = useMemo(
    () => ({ cities, loading, error, selectedCity, setSelectedCity, citySource }),
    [cities, loading, error, selectedCity, setSelectedCity, citySource],
  );

  return (
    <CarServiceCityContext.Provider value={value}>
      {children}
    </CarServiceCityContext.Provider>
  );
};

export const useCarServiceCity = () => useContext(CarServiceCityContext);
